import { useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
} from "@mui/material";
import { ReactElement, useState } from "react";

import {
  ListShortCodeOptInKeywordsQuery,
  ListShortCodeOptInKeywordsQueryVariables,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import { PageHeader } from "~frontend/components/PageHeader";
import { TableBox } from "~frontend/components/Table";
import { LIST_SHORT_CODE_OPT_IN_KEYWORDS } from "~frontend/graphql/shortcode";

import CreateKeywordDialog from "./CreateKeywordDialog";
import { CopyKeywordLinkDialog } from "./KeywordDialogForm";
import OptInKeywordsTable from "./OptInKeywordsTable";
import UpdateKeywordDialog from "./UpdateKeywordDialog";
import { ShortcodeOptInKeyword } from "./types";

export default function OptInKeywords({
  shortcodeId,
  shortcodePhoneNumber,
  variant = "default",
}: {
  shortcodeId: string;
  shortcodePhoneNumber: string;
  variant?: "default" | "slim";
}): ReactElement {
  const { data, loading, error } = useQuery<
    ListShortCodeOptInKeywordsQuery,
    ListShortCodeOptInKeywordsQueryVariables
  >(LIST_SHORT_CODE_OPT_IN_KEYWORDS, {
    variables: {
      input: {
        shortcodeId: shortcodeId,
      },
    },
  });

  const [createKeywordDialogOpen, setCreateKeywordDialogOpen] = useState(false);
  const [editingKeyword, setEditingKeyword] =
    useState<ShortcodeOptInKeyword | null>(null);
  const [copyingKeyword, setCopyingKeyword] =
    useState<ShortcodeOptInKeyword | null>(null);

  return (
    <Box>
      {variant === "default" && (
        <PageHeader>
          <FlexBox justifyContent="space-between" alignItems="center">
            <span>Opt In Keywords</span>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setCreateKeywordDialogOpen(true)}
            >
              + New Opt In Keyword
            </Button>
          </FlexBox>
        </PageHeader>
      )}

      <AsyncWrapper loading={loading} errors={[error]} data={data}>
        {data =>
          variant === "default" ? (
            <TableBox>
              <OptInKeywordsTable
                keywords={data.listShortCodeOptInKeywords}
                shortcodeId={shortcodeId}
                openEditDialog={setEditingKeyword}
                openCopyKeywordLinkDialog={setCopyingKeyword}
              />
            </TableBox>
          ) : (
            <>
              <Typography paddingBottom={2}>
                Custom Keywords only apply to your short code number, and will
                not be received by your other opt in senders.
              </Typography>

              <Card>
                <CardHeader title={`Custom Keywords `} />
                <CardContent>
                  <OptInKeywordsTable
                    keywords={data.listShortCodeOptInKeywords}
                    shortcodeId={shortcodeId}
                    openEditDialog={setEditingKeyword}
                    openCopyKeywordLinkDialog={setCopyingKeyword}
                  />
                  <FlexBox justifyContent="flex-end">
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => setCreateKeywordDialogOpen(true)}
                    >
                      <Typography component={"span"} variant="h4Caps">
                        + New Custom Keyword
                      </Typography>
                    </Button>
                  </FlexBox>
                </CardContent>
              </Card>
            </>
          )
        }
      </AsyncWrapper>
      <CreateKeywordDialog
        shortcodeId={shortcodeId}
        shortcodePhoneNumber={shortcodePhoneNumber}
        open={createKeywordDialogOpen}
        onClose={() => setCreateKeywordDialogOpen(false)}
      />
      {editingKeyword ? (
        <UpdateKeywordDialog
          keyword={editingKeyword}
          onClose={() => setEditingKeyword(null)}
          shortcodePhoneNumber={shortcodePhoneNumber}
        />
      ) : (
        <></>
      )}
      {copyingKeyword && (
        <CopyKeywordLinkDialog
          onClose={() => setCopyingKeyword(null)}
          shortcodePhoneNumber={shortcodePhoneNumber}
          keyword={copyingKeyword?.normalizedKeyword}
        />
      )}
    </Box>
  );
}
